import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

const OpsStatsForm = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();

    const [selectedMonth, setSelectedMonth] = useState(currentMonth);
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [selectedPropertyID, setSelectedPropertyID] = useState('');
    const [properties, setProperties] = useState([]);
    const [form, setForm] = useState({
        OccupancyCertificate: false,
        FireClearance: false
    });
    const [isLoading, setIsLoading] = useState(false);
    const [sessionExpired, setSessionExpired] = useState(false);

    const { apiFetch, logout } = useAuth();
    const navigate = useNavigate();

    const months = [
        { value: 1, name: "January" }, { value: 2, name: "February" }, { value: 3, name: "March" },
        { value: 4, name: "April" }, { value: 5, name: "May" }, { value: 6, name: "June" },
        { value: 7, name: "July" }, { value: 8, name: "August" }, { value: 9, name: "September" },
        { value: 10, name: "October" }, { value: 11, name: "November" }, { value: 12, name: "December" }
    ];
    const years = [currentYear - 2, currentYear - 1, currentYear];

    const isMonthDisabled = (monthValue) => {
        if (selectedYear > currentYear) return false;
        if (selectedYear === currentYear && monthValue > currentMonth) return true;
        return false;
    };

    const getSelectedPeriod = () => `${selectedYear}${selectedMonth.toString().padStart(2, '0')}`;

    // Fetch properties on mount
    useEffect(() => {
        const fetchProperties = async () => {
            try {
                const response = await apiFetch('/api/Property/all');
                const data = await response.json();
                if (data.success && Array.isArray(data.data)) {
                    const active = data.data.filter(p => p.active);
                    setProperties(active);
                    if (active.length > 0) setSelectedPropertyID(active[0].propertyID);
                }
            } catch (e) {
                Sentry.captureException(e);
                if (e.message && e.message.includes('Session expired')) {
                    setSessionExpired(true);
                    logout();
                }
            }
        };
        fetchProperties();
        // eslint-disable-next-line
    }, []);

    // Fetch data for selected period + property
    useEffect(() => {
        if (!selectedPropertyID) return;

        const fetchData = async () => {
            setIsLoading(true);
            try {
                const response = await apiFetch(`/api/Ops/OpsStatsByPeriod?Period=${getSelectedPeriod()}`);
                const data = await response.json();
                if (data.success && Array.isArray(data.data)) {
                    const periodData = data.data.find(
                        x => x.period === parseInt(getSelectedPeriod(), 10) && x.propertyID === selectedPropertyID
                    );
                    if (periodData) {
                        setForm({
                            OccupancyCertificate: periodData.occupancyCertificate,
                            FireClearance: periodData.fireClearance
                        });
                    } else {
                        setForm({ OccupancyCertificate: false, FireClearance: false });
                    }
                } else {
                    setForm({ OccupancyCertificate: false, FireClearance: false });
                }
            } catch (e) {
                Sentry.captureException(e);
                if (e.message && e.message.includes('Session expired')) {
                    setSessionExpired(true);
                    logout();
                    return;
                }
                setForm({ OccupancyCertificate: false, FireClearance: false });
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
        // eslint-disable-next-line
    }, [selectedMonth, selectedYear, selectedPropertyID]);

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: checked }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!selectedPropertyID) {
            toast.error("Please select a property.");
            return;
        }
        setIsLoading(true);
        try {
            const payload = {
                Period: parseInt(getSelectedPeriod(), 10),
                PropertyID: selectedPropertyID,
                OccupancyCertificate: form.OccupancyCertificate,
                FireClearance: form.FireClearance
            };
            const response = await apiFetch('/api/Ops/OpsStats', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (data.success) {
                toast.success("Operations compliance data saved successfully");
            }
        } catch (e) {
            Sentry.captureException(e);
            if (e.message && e.message.includes('Session expired')) {
                setSessionExpired(true);
                logout();
                return;
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (sessionExpired) {
        return (
            <div className="error-message">
                <p>Your session has expired. Please log in again.</p>
                <button onClick={() => navigate('/')}>Go to Login</button>
            </div>
        );
    }

    return (
        <div className="ops-stats-container">
            <h1>Operations Compliance Input</h1>
            <div className="period-selector">
                <label>Property:</label>
                <select value={selectedPropertyID} onChange={e => setSelectedPropertyID(Number(e.target.value))}>
                    {properties.map(p => (
                        <option key={p.propertyID} value={p.propertyID}>{p.propertyName}</option>
                    ))}
                </select>
                <label>Month:</label>
                <select value={selectedMonth} onChange={e => setSelectedMonth(Number(e.target.value))}>
                    {months.map(month => (
                        <option key={month.value} value={month.value} disabled={isMonthDisabled(month.value)}>
                            {month.name}
                        </option>
                    ))}
                </select>
                <label>Year:</label>
                <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}>
                    {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                    ))}
                </select>
            </div>
            <form onSubmit={handleSave}>
                <div>
                    <label>
                        <input
                            type="checkbox"
                            name="OccupancyCertificate"
                            checked={form.OccupancyCertificate}
                            onChange={handleCheckboxChange}
                            disabled={isLoading}
                        />
                        {' '}Occupancy certificate compliance
                    </label>
                </div>
                <div>
                    <label>
                        <input
                            type="checkbox"
                            name="FireClearance"
                            checked={form.FireClearance}
                            onChange={handleCheckboxChange}
                            disabled={isLoading}
                        />
                        {' '}Fire clearance compliance
                    </label>
                </div>
                <button type="submit" disabled={isLoading || !selectedPropertyID}>
                    {isLoading ? 'Saving...' : 'Save'}
                </button>
            </form>
        </div>
    );
};

export default OpsStatsForm;